import { useLang } from "@/context/LanguageContext";
import { ECOSYSTEM } from "@/lib/ecosystem";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  LayoutGrid, ShieldCheck, Shield, RefreshCw, ClipboardList, PersonStanding, MessagesSquare, Leaf, ExternalLink, Check,
} from "lucide-react";

const ICONS = { ShieldCheck, Shield, RefreshCw, ClipboardList, PersonStanding, MessagesSquare, Leaf };

export default function FamilySwitcher({ variant = "default" }) {
  const { t, lang } = useLang();
  const onDark = variant === "dark";

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          data-testid="family-switcher-button"
          aria-label={t("common.family")}
          className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-sm text-xs font-semibold transition-all duration-200 ${
            onDark ? "text-white/90 hover:bg-white/10" : "text-[#4B5950] hover:bg-[#EAF0EA] border border-[#DCE3DD]"
          }`}
        >
          <LayoutGrid className="h-4 w-4" />
          <span className="hidden sm:inline">{t("common.familyShort")}</span>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-72 p-0">
        <DropdownMenuLabel className="px-4 py-3 text-xs uppercase tracking-[0.15em] text-[#4B5950]">
          {t("common.family")}
        </DropdownMenuLabel>
        <DropdownMenuSeparator className="my-0" />
        <div className="max-h-96 overflow-y-auto es-scroll py-1">
          {ECOSYSTEM.map((p) => {
            const Icon = ICONS[p.icon] || Leaf;
            const body = (
              <>
                <div className={`h-9 w-9 grid place-items-center rounded-sm shrink-0 ${p.current ? "bg-[#275C3F]" : "bg-[#EAF0EA]"}`}>
                  <Icon className={`h-[18px] w-[18px] ${p.current ? "text-white" : "text-[#275C3F]"}`} />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-[#121A14] flex items-center gap-1.5">
                    {p.name}
                    {!p.current && <ExternalLink className="h-3 w-3 text-[#9CA99F]" />}
                  </p>
                  <p className="text-xs text-[#4B5950] truncate">{p.tag[lang] || p.tag.en}</p>
                </div>
                {p.current && <Check className="h-4 w-4 text-[#275C3F] shrink-0" />}
              </>
            );
            return p.current ? (
              <div key={p.name} data-testid="family-item-current" className="flex items-center gap-3 px-3 py-2.5 bg-[#F8FAF8]">
                {body}
              </div>
            ) : (
              <a
                key={p.name}
                data-testid={`family-item-${p.domain}`}
                href={p.url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 px-3 py-2.5 hover:bg-[#EAF0EA] transition-colors"
              >
                {body}
              </a>
            );
          })}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
